import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import api from '@/admin/lib/api'
import type {
  TriggerCreateBody,
  TriggerDetail,
  TriggerKeySuggestion,
  TriggerKind,
  TriggerListRow,
} from './types'

const listKey = (kind?: TriggerKind | 'all') => ['email-triggers', kind ?? 'all']
const detailKey = (uuid: string) => ['email-triggers', 'detail', uuid]

export function useEmailTriggerList(kind?: TriggerKind | 'all') {
  return useQuery<TriggerListRow[]>({
    queryKey: listKey(kind),
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (kind && kind !== 'all') params.trigger_kind = kind
      const res = await api.get('/admin/email-triggers/', { params })
      return res.data
    },
  })
}

export function useEmailTrigger(uuid: string | undefined) {
  return useQuery<TriggerDetail>({
    queryKey: detailKey(uuid ?? ''),
    enabled: Boolean(uuid),
    queryFn: async () => {
      const res = await api.get(`/admin/email-triggers/${uuid}/`)
      return res.data
    },
  })
}

export function useCreateTrigger() {
  const qc = useQueryClient()
  return useMutation<TriggerDetail, unknown, TriggerCreateBody>({
    mutationFn: async (payload) => {
      const res = await api.post('/admin/email-triggers/', payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['email-triggers'] })
    },
  })
}

export function useUpdateTrigger(uuid: string) {
  const qc = useQueryClient()
  return useMutation<TriggerDetail, unknown, Partial<TriggerCreateBody>>({
    mutationFn: async (payload) => {
      const res = await api.patch(`/admin/email-triggers/${uuid}/`, payload)
      return res.data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['email-triggers'] })
      qc.setQueryData(detailKey(uuid), data)
    },
  })
}

export function useDeleteTrigger() {
  const qc = useQueryClient()
  return useMutation<void, unknown, { uuid: string }>({
    mutationFn: async ({ uuid }) => {
      await api.delete(`/admin/email-triggers/${uuid}/`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['email-triggers'] })
    },
  })
}

export function useTriggerKeys(
  triggerKind: TriggerKind | undefined,
  workflowUuid: string | null | undefined,
) {
  return useQuery<TriggerKeySuggestion[]>({
    queryKey: ['email-trigger-keys', triggerKind ?? 'none', workflowUuid ?? 'all'],
    enabled: Boolean(triggerKind),
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (triggerKind) params.trigger_kind = triggerKind
      if (workflowUuid) params.workflow_uuid = workflowUuid
      const res = await api.get('/admin/email-triggers/keys/', { params })
      return res.data
    },
  })
}
